// src/components/Hero.jsx
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCms } from '../hooks/useCms';
import { getPublicMakes } from '../../services/make.service';
import { getCityDropdown } from '../../services/city.service';
import api from '../../services/api';

type Option = { ID: string; name: string };

const priceRanges = [
  { label: 'Any Price', minPrice: '', maxPrice: '' },
  { label: 'Under 100,000 MAD', minPrice: '', maxPrice: '100000' },
  { label: '100k - 200k MAD', minPrice: '100000', maxPrice: '200000' },
  { label: '200k - 500k MAD', minPrice: '200000', maxPrice: '500000' },
  { label: '500k+ MAD', minPrice: '500000', maxPrice: '' },
];

export function Hero() {
  const { t } = useTranslation('home');
  const { get } = useCms('home');
  const navigate = useNavigate();

  const [bodyTypes, setBodyTypes] = useState<Option[]>([]);
  const [makes, setMakes] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [bodyType, setBodyType] = useState('');
  const [make, setMake] = useState('');
  const [city, setCity] = useState('');
  const [priceRange, setPriceRange] = useState('0');
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  useEffect(() => {
    const loadOptions = async () => {
      const results = await Promise.allSettled([
        api.post('/body-type/dropdown', {}),
        getPublicMakes(),
        getCityDropdown(),
      ]);

      if (results[0].status === 'fulfilled') {
        setBodyTypes((results[0].value.data.data || []).map((item: any) => ({ ID: item.ID, name: item.BodyTypeName })));
      }
      if (results[1].status === 'fulfilled') {
        setMakes((results[1].value || []).map((item: any) => ({ ID: item.ID, name: item.MakeName })));
      }
      if (results[2].status === 'fulfilled') {
        setCities((results[2].value || []).map((item: any) => ({ ID: item.ID, name: item.CityName })));
      }
      if (results.some((result) => result.status === 'rejected')) {
        console.error('One or more homepage search options could not be loaded', results);
      }

      setIsLoadingOptions(false);
    };
    void loadOptions();
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    const selectedPrice = priceRanges[Number(priceRange)];
    if (bodyType) params.set('bodyType', bodyType);
    if (make) params.set('make', make);
    if (city) params.set('city', city);
    if (selectedPrice.minPrice) params.set('minPrice', selectedPrice.minPrice);
    if (selectedPrice.maxPrice) params.set('maxPrice', selectedPrice.maxPrice);
    navigate(`/search${params.size ? `?${params.toString()}` : ''}`);
  };

  // ---- Translations for dropdown options ----
  // We'll use the translation keys defined in home.json
  const getTranslatedOption = (key: string, fallback: string) => {
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch {
      return fallback;
    }
  };

  return (
    <div className="relative bg-gradient-to-br from-[#2D323A] via-[#1E1E26] to-[#121212] text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-6">
            {get('hero.heroTitle', t('hero.title'))}
            <br />
            <span className="text-[#1E6DFF]">{get('hero.heroHighlight', t('hero.subtitle'))}</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#BFC2C7] mb-12">
            {get('hero.heroDescription', t('hero.description'))}
          </p>

          {/* Search Box */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 backdrop-blur-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Vehicle Type */}
              <div className="relative">
                <label className="block text-sm text-[#2D323A] mb-2 text-left">
                  {t('hero.search.vehicle_type')}
                </label>
                <select
                  value={bodyType}
                  onChange={(event) => setBodyType(event.target.value)}
                  disabled={isLoadingOptions}
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] text-[#2D323A] focus:outline-none focus:ring-2 focus:ring-[#1E6DFF] focus:border-transparent disabled:opacity-60"
                >
                  <option value="">{t('hero.search.vehicle_type_options.all')}</option>
                  {bodyTypes.map((item) => (
                    <option key={item.ID} value={item.ID}>{item.name}</option>
                  ))}
                </select>
              </div>

              {/* Make */}
              <div className="relative">
                <label className="block text-sm text-[#2D323A] mb-2 text-left">
                  {t('hero.search.make')}
                </label>
                <select
                  value={make}
                  onChange={(event) => setMake(event.target.value)}
                  disabled={isLoadingOptions}
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] text-[#2D323A] focus:outline-none focus:ring-2 focus:ring-[#1E6DFF] focus:border-transparent disabled:opacity-60"
                >
                  <option value="">{t('hero.search.make_options.all')}</option>
                  {makes.map((item) => (
                    <option key={item.ID} value={item.name}>{item.name}</option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div className="relative">
                <label className="block text-sm text-[#2D323A] mb-2 text-left">
                  {t('hero.search.city')}
                </label>
                <select
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  disabled={isLoadingOptions}
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] text-[#2D323A] focus:outline-none focus:ring-2 focus:ring-[#1E6DFF] focus:border-transparent disabled:opacity-60"
                >
                  <option value="">{t('hero.search.city_options.all')}</option>
                  {cities.map((item) => (
                    <option key={item.ID} value={item.ID}>{item.name}</option>
                  ))}
                </select>
              </div>

              {/* Budget */}
              <div className="relative">
                <label className="block text-sm text-[#2D323A] mb-2 text-left">
                  {t('hero.search.budget')}
                </label>
                <select
                  value={priceRange}
                  onChange={(event) => setPriceRange(event.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F5F7FA] text-[#2D323A] focus:outline-none focus:ring-2 focus:ring-[#1E6DFF] focus:border-transparent"
                >
                  {priceRanges.map((range, index) => {
                    // Translate price range labels – only for fixed ones, not dynamic ones.
                    let label = range.label;
                    if (label === 'Any Price') label = t('hero.search.budget_options.any');
                    else if (label === 'Under 100,000 MAD') label = t('hero.search.budget_options.under_100k');
                    else if (label === '100k - 200k MAD') label = t('hero.search.budget_options.100k_200k');
                    else if (label === '200k - 500k MAD') label = t('hero.search.budget_options.200k_500k');
                    else if (label === '500k+ MAD') label = t('hero.search.budget_options.500k_plus');
                    return <option key={range.label} value={index}>{label}</option>;
                  })}
                </select>
              </div>

              {/* Search Button */}
              <div className="relative flex items-end">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={isLoadingOptions}
                  className="w-full px-6 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:cursor-wait disabled:opacity-70"
                >
                  <Search className="w-5 h-5" />
                  <span>{t('hero.search.button')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E6DFF] rounded-full filter blur-3xl opacity-10" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#BFC2C7] rounded-full filter blur-3xl opacity-10" />
    </div>
  );
}